import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { getCartPrice, placeOrder } from "../../../api/user/hotels";
import { useTranslation } from "../../../hooks/useTranslation";

export default function HotelCartGuest() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { hotelId, roomId, items = [] } = state || {};
  const {t} = useTranslation()

  const [cart, setCart] = useState(items);
  const [debouncedCart, setDebouncedCart] = useState(items);
  const [formData, setFormData] = useState({
    specialInstructions: "",
    deliveryTime: "",
    deliveryMethod: "deliver to room",
  });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedCart(cart), 300);
    return () => clearTimeout(t);
  }, [cart]);

  const { data, isLoading } = useQuery({
    queryKey: ["guest-cart-price", hotelId, roomId, debouncedCart],
    queryFn: () => getCartPrice({ hotelId, roomId, items: debouncedCart }),
    enabled: !!hotelId && !!roomId && !!debouncedCart?.length,
    keepPreviousData: true,
  });

  const charges = data?.data?.charges;

  const orderMutation = useMutation({
    mutationFn: (payload) => placeOrder(payload),
    onSuccess: ({ data }) => {
      const { url } = data || {};
      if (url) window.open(url, "_blank");
      toast.success(t("cart").redirectingToPayment || "Redirecting to payment...");
      setTimeout(() => navigate("/"), 2500);
    },
    onError: (error) => {
      const msg = error?.response?.data?.message || t("cart").checkoutFailed || "Checkout failed";
      toast.error(msg);
    },
  });

  const handlePlaceOrder = () => {
    if (!cart.length) return;
    orderMutation.mutate({
      hotelId,
      roomId,
      items: cart,
      ...formData,
    });
  };

  return (
    <div className="max-w-4xl mx-auto p-6 mt-8 mb-8 bg-white border rounded">
      <h1 className="text-2xl font-semibold mb-4">{t("cart").yourCart || "Your Cart"}</h1>

      <div className="space-y-2 mb-4">
        {cart.map((ci) => (
          <div key={ci.itemId} className="flex items-center justify-between border p-2 rounded">
            <div>
              <p className="font-medium">{t("cart").item || "Item"} {ci.itemId}</p>
              {ci.note && <p className="text-sm text-gray-600">{t("cart").note || "Note"}: {ci.note}</p>}
            </div>
            <div className="flex items-center gap-2">
              <button
                className="px-2 border rounded"
                onClick={() =>
                  setCart((prev) =>
                    prev.map((p) =>
                      p.itemId === ci.itemId ? { ...p, quantity: Math.max(0, p.quantity - 1) } : p
                    ).filter((p) => p.quantity > 0)
                  )
                }
              >
                -
              </button>
              <span>{ci.quantity}</span>
              <button
                className="px-2 border rounded"
                onClick={() =>
                  setCart((prev) => prev.map((p) => (p.itemId === ci.itemId ? { ...p, quantity: p.quantity + 1 } : p)))
                }
              >
                +
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <textarea
            className="w-full border rounded p-2"
            placeholder={t("cart").specialInstructionsPlaceholder || "Special instructions"}
            value={formData.specialInstructions}
            onChange={(e) => setFormData((s) => ({ ...s, specialInstructions: e.target.value }))}
          />
          <input
            className="w-full border rounded p-2"
            placeholder={t("cart").preferredDeliveryTime || "Preferred delivery time"}
            value={formData.deliveryTime}
            onChange={(e) => setFormData((s) => ({ ...s, deliveryTime: e.target.value }))}
          />
          <select
            className="w-full border rounded p-2"
            value={formData.deliveryMethod}
            onChange={(e) => setFormData((s) => ({ ...s, deliveryMethod: e.target.value }))}
          >
            <option>{t("cart").deliverToRoom || "deliver to room"}</option>
            <option>{t("cart").pickupAtCounter || "pickup at counter"}</option>
          </select>
        </div>

        <div className="border rounded p-3">
          <h2 className="font-semibold mb-2">{t("cart").charges || "Charges"}</h2>
          {isLoading ? (
            <p>{t("cart").loading || "Loading..."}</p>
          ) : charges ? (
            <div className="text-sm space-y-1">
              <p>{t("cart").subtotal || "Subtotal:"} {charges.subTotal}</p>
              <p>{t("cart").taxes || "Taxes:"} {charges.taxes}</p>
              <p>{t("cart").total || "Total:"} {charges.total}</p>
            </div>
          ) : (
            <p className="text-gray-600">{t("restaurantmenu").addItemsToSeePrice}</p>
          )}
          <button
            disabled={!cart.length}
            onClick={handlePlaceOrder}
            className="mt-3 bg-[#4CAF50] disabled:opacity-50 text-white font-bold py-2 px-4 rounded"
          >
            {t("cart").proceedToPay || "Proceed to Pay"}
          </button>
        </div>
      </div>
    </div>
  );
}


